import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AvailabilityCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Mock availability data
  const availableSlots = {
    '2025-01-20': ['10:00 AM', '2:00 PM', '4:00 PM'],
    '2025-01-21': ['9:00 AM', '11:00 AM', '3:00 PM'],
    '2025-01-22': ['10:00 AM', '1:00 PM'],
    '2025-01-23': ['9:00 AM', '2:00 PM', '4:00 PM'],
    '2025-01-24': ['11:00 AM', '3:00 PM'],
    '2025-01-27': ['10:00 AM', '2:00 PM', '4:00 PM'],
    '2025-01-28': ['9:00 AM', '1:00 PM', '3:00 PM'],
    '2025-01-29': ['10:00 AM', '2:00 PM'],
    '2025-01-30': ['9:00 AM', '11:00 AM', '4:00 PM'],
    '2025-01-31': ['10:00 AM', '3:00 PM']
  };

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(new Date(year, month, day));
    }
    
    return days;
  };

  const formatDate = (date) => {
    return date?.toISOString()?.split('T')?.[0];
  };

  const isDateAvailable = (date) => {
    if (!date) return false;
    const dateStr = formatDate(date);
    return availableSlots?.[dateStr] && availableSlots?.[dateStr]?.length > 0;
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateSelect = (date) => {
    if (isPastDate(date) || !isDateAvailable(date)) return;
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleBookConsultation = () => {
    if (selectedDate && selectedTime) {
      // Mock booking logic
      alert(`Consultation booked for ${selectedDate?.toLocaleDateString()} at ${selectedTime}`);
      setSelectedDate(null);
      setSelectedTime(null);
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth?.setMonth(currentMonth?.getMonth() + direction);
    setCurrentMonth(newMonth);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="bg-surface rounded-xl p-6 shadow-subtle">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Schedule a Consultation
        </h3>
        <p className="text-muted-foreground text-sm">
          Book a free 30-minute consultation to discuss your project.
        </p>
      </div>
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateMonth(-1)}
          disabled={currentMonth?.getMonth() === new Date()?.getMonth() && 
                   currentMonth?.getFullYear() === new Date()?.getFullYear()}
        >
          <Icon name="ChevronLeft" size={20} />
        </Button>
        
        <h4 className="text-lg font-medium text-foreground">
          {monthNames?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
        </h4>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateMonth(1)}
        >
          <Icon name="ChevronRight" size={20} />
        </Button>
      </div>
      {/* Calendar Grid */}
      <div className="mb-6">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames?.map(day => (
            <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {days?.map((date, index) => {
            if (!date) {
              return <div key={index} className="h-10" />;
            }

            const isAvailable = isDateAvailable(date);
            const isPast = isPastDate(date);
            const isSelected = selectedDate && formatDate(date) === formatDate(selectedDate);

            return (
              <button
                key={index}
                onClick={() => handleDateSelect(date)}
                disabled={isPast || !isAvailable}
                className={`h-10 text-sm rounded-lg transition-all duration-200 ${
                  isSelected
                    ? 'bg-primary text-primary-foreground'
                    : isAvailable && !isPast
                    ? 'bg-accent/10 text-foreground hover:bg-accent/20'
                    : isPast
                    ? 'text-muted-foreground cursor-not-allowed'
                    : 'text-muted-foreground cursor-not-allowed'
                }`}
              >
                {date?.getDate()}
              </button>
            );
          })}
        </div>
      </div>
      {/* Time Slots */}
      {selectedDate && (
        <div className="mb-6">
          <h5 className="text-sm font-medium text-foreground mb-3">
            Available times for {selectedDate?.toLocaleDateString()}
          </h5>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {availableSlots?.[formatDate(selectedDate)]?.map((time, index) => (
              <button
                key={index}
                onClick={() => handleTimeSelect(time)}
                className={`p-2 text-sm rounded-lg border transition-all duration-200 ${
                  selectedTime === time
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:border-primary/50'
                }`}
              >
                {time}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Book Button */}
      {selectedDate && selectedTime && (
        <div className="pt-4 border-t border-border">
          <Button
            variant="default"
            fullWidth
            onClick={handleBookConsultation}
            iconName="Calendar"
            iconPosition="left"
          >
            Book Consultation
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            You'll receive a calendar invite after booking
          </p>
        </div>
      )}
      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-center space-x-4 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-accent/10 rounded"></div>
            <span className="text-muted-foreground">Available</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-primary rounded"></div>
            <span className="text-muted-foreground">Selected</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;